import * as formik from 'formik';
import * as fabric from 'office-ui-fabric-react';
import { DropdownMenuItemType } from 'office-ui-fabric-react';
import { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import * as api from '../../../../../api/generated';
import { KendoLoading } from '../../../../../components';
import { ILocationItem } from '../../../../../components/custom/LocationTree';
import { IWindowComponentProps } from '../../../../../components/windows/WindowContext';
import { priorityLevel } from '../../../../../enums';
import { IServiceTerminalQueueLocation } from './StqlList';

interface StqlCreateModalWindowProps {
    queueId: number;
    organizationInformationId: number;
    refetchStqlList(): Promise<void>;
    basedOnInitialValues: IServiceTerminalQueueLocation | null;
    openStqlCreateModalWindow: (basedOnInitialValues: IServiceTerminalQueueLocation | null) => Promise<void>;
}

interface ITerminalService {
    id: number;
    serviceId: number;
    serviceName: string | null;
    terminalId: number;
    terminalName: string | null;
}

const validationSchema = Yup.object<IServiceTerminalQueueLocation>().shape({
    terminalServiceId: Yup.number().required('Šis laukas yra privalomas'),
    locationId: Yup.number().required('Šis laukas yra privalomas'),
    queueId: Yup.number().required('Šis laukas yra privalomas'),
    priorityLevel: Yup.number().required('Šis laukas yra privalomas')
});

const StqlCreateModalWindow: FC<IWindowComponentProps<StqlCreateModalWindowProps>> = (props) => {
    const {
        componentProps: { refetchStqlList, openStqlCreateModalWindow, queueId, organizationInformationId, basedOnInitialValues },
        onClose
    } = props;

    const initialValues = { queueId: queueId } as IServiceTerminalQueueLocation;
    const [locations, setLocations] = useState<ILocationItem[] | null>(null);
    const [terminalServices, setTerminalServices] = useState<ITerminalService[] | null>(null);
    const [createNext, setCreateNext] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        try {
            const fetchForm = async () => {
                const [locationsResp, terminalServicesResp] = await Promise.all([
                    api.administration.getLocations(null),
                    api.terminalServices.getAllTerminalServices(organizationInformationId)
                ]);

                setLocations(locationsResp);
                setTerminalServices(terminalServicesResp);
            };
            fetchForm();
        } catch (ex) {
        } finally {
            setIsLoading(false);
        }
    }, []);

    const handleSubmit = async (values: IServiceTerminalQueueLocation, formikActions: formik.FormikHelpers<IServiceTerminalQueueLocation>) => {
        formikActions.setStatus();
        try {
            await api.servicesTerminalsQueuesLocations.createServiceTerminalQueueLocation(values);
            toast.success('Eilė informacija sėkmingai išsaugota');
            await refetchStqlList();
            onClose();
            if (createNext) {
                await openStqlCreateModalWindow(values);
            }
        } catch (ex) {
            formikActions.setStatus(ex);
            toast.error(ex.error[0]);
        } finally {
            formikActions.setSubmitting(false);
        }
    };

    const mapLocationsOptions = (allLocations: ILocationItem[]): fabric.IDropdownOption[] => {
        var options: fabric.IDropdownOption[] = [];

        allLocations.map((x) => {
            options.push({ key: x.id, text: x.name ? x.name : '' });
        });
        return options;
    };
    const mapTerminalServicesOptions = (terminalServices: ITerminalService[]): fabric.IDropdownOption[] => {
        var options: fabric.IDropdownOption[] = [];

        if (terminalServices.length > 0) {
            var currentTerminalId: number = terminalServices[0].terminalId;
            options.push({
                key: `terminal${terminalServices[0].terminalId}`,
                text: `Terminalas - „${terminalServices[0].terminalName}“` ?? '',
                itemType: DropdownMenuItemType.Header
            });

            terminalServices.map((x) => {
                if (currentTerminalId == x.terminalId) {
                } else {
                    options.push({
                        key: `terminal${x.terminalId}`,
                        text: `Terminalas - „${x.terminalName}“` ?? '',
                        itemType: DropdownMenuItemType.Header
                    });
                    currentTerminalId = x.terminalId;
                }
                options.push({ key: x.id, text: x.serviceName ? `${x.serviceName}` : '' });
            });
        }
        return options;
    };

    return (
        <>
            {isLoading ? (
                <KendoLoading />
            ) : (
                <>
                    {terminalServices && locations && (
                        <formik.Formik
                            validationSchema={validationSchema}
                            initialValues={basedOnInitialValues === null ? initialValues : { ...basedOnInitialValues, name: '' }}
                            onSubmit={handleSubmit}
                        >
                            {(formikProps) => (
                                <formik.Form>
                                    <div className="ms-Grid-row">
                                        <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                            <formik.Field
                                                name="terminalServiceId"
                                                render={({ field, form }: formik.FieldProps<number>) => (
                                                    <fabric.Dropdown
                                                        {...field}
                                                        options={mapTerminalServicesOptions(terminalServices!)}
                                                        label="Terminalo paslaugos"
                                                        placeholder="Pasirinkite terminalo paslaugą"
                                                        onChange={({}, value: any) => form.setFieldValue(field.name, value.key)}
                                                        defaultSelectedKey={field.value}
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                            <formik.Field
                                                name="locationId"
                                                render={({ field, form }: formik.FieldProps<number>) => (
                                                    <fabric.Dropdown
                                                        {...field}
                                                        options={mapLocationsOptions(locations!)}
                                                        label="Lokacija"
                                                        placeholder="Pasirinkite lokaciją"
                                                        onChange={({}, value: any) => form.setFieldValue(field.name, value.key)}
                                                        defaultSelectedKey={field.value}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <div className="ms-Grid-row">
                                        <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                            <formik.Field
                                                name="priorityLevel"
                                                render={({ field, form }: formik.FieldProps<number>) => (
                                                    <fabric.Dropdown
                                                        {...field}
                                                        options={priorityLevel.displayTypesArray()}
                                                        label="Prioriteto lygis"
                                                        placeholder="Pasirinkite prioriteto lygį"
                                                        onChange={({}, value: any) => form.setFieldValue(field.name, value.key)}
                                                        defaultSelectedKey={field.value}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'center', marginTop: '30px' }}>
                                        <fabric.Checkbox
                                            label="Kurti kitą"
                                            checked={createNext}
                                            onChange={() => setCreateNext(!createNext)}
                                        ></fabric.Checkbox>
                                        <fabric.PrimaryButton
                                            className="ms-slideRightIn40 ms-textAlignRight"
                                            text="Išsaugoti"
                                            disabled={formikProps.isSubmitting || !formikProps.isValid || !formikProps.dirty}
                                            type="submit"
                                        />
                                    </div>
                                </formik.Form>
                            )}
                        </formik.Formik>
                    )}
                </>
            )}
        </>
    );
};

export default StqlCreateModalWindow;
